import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getDownloadUrl } from '@/api/client';
import { getErrorMessage } from '@/utils/error';

interface FilePreviewProps {
  open: boolean;
  fileKey: string;
  onClose: () => void;
  onDownload: (key: string) => void;
}

/**
 * ファイルプレビューダイアログ
 */
const FilePreview: React.FC<FilePreviewProps> = ({
  open,
  fileKey,
  onClose,
  onDownload,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileName = fileKey.split('/').pop() || '';
  const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';

  // プレビュー可能な拡張子
  const previewableExtensions = ['pdf', 'txt', 'md', 'html', 'json', 'csv', 'png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'];
  const isPreviewable = previewableExtensions.includes(fileExtension);

  // 画像かどうか
  const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'];
  const isImage = imageExtensions.includes(fileExtension);

  // テキストかどうか
  const textExtensions = ['txt', 'md', 'html', 'json', 'csv'];
  const isText = textExtensions.includes(fileExtension);

  useEffect(() => {
    if (open && fileKey && isPreviewable) {
      loadPreview();
    }

    return () => {
      setPreviewUrl(null);
      setTextContent(null);
      setError(null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fileKey]);

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    setTextContent(null);

    try {
      const result = await getDownloadUrl(fileKey);
      if (result.success && result.data) {
        const url = result.data.downloadUrl;
        setPreviewUrl(url);

        // テキスト系はS3のContent-Typeにcharsetが付かず文字化けするため、
        // 明示的にUTF-8でデコードして表示する
        if (isText) {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`ファイルの取得に失敗しました (${response.status})`);
          }
          const buffer = await response.arrayBuffer();
          const decoded = new TextDecoder('utf-8').decode(buffer);
          setTextContent(decoded);
        }
      } else {
        throw new Error(result.error || 'URLの取得に失敗しました');
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'エラーが発生しました'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenInNewTab = () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank');
    }
  };

  const renderPreviewContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      );
    }

    if (!isPreviewable) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            このファイル形式はプレビューできません
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ダウンロードしてご確認ください
          </Typography>
        </Box>
      );
    }

    if (!previewUrl) {
      return null;
    }

    if (isImage) {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <img
            src={previewUrl}
            alt={fileName}
            style={{
              maxWidth: '100%',
              maxHeight: '60vh',
              objectFit: 'contain',
            }}
          />
        </Box>
      );
    }

    if (fileExtension === 'pdf') {
      return (
        <Box sx={{ height: '60vh' }}>
          <iframe
            src={previewUrl}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
            title={fileName}
          />
        </Box>
      );
    }

    if (isText) {
      if (textContent === null) {
        return null;
      }

      // HTMLはレンダリング表示（UTF-8デコード済みの内容をsrcDocに渡す）
      if (fileExtension === 'html') {
        return (
          <Box sx={{ height: '60vh' }}>
            <iframe
              srcDoc={textContent}
              width="100%"
              height="100%"
              style={{ border: '1px solid #ddd', borderRadius: 4 }}
              title={fileName}
              sandbox=""
            />
          </Box>
        );
      }

      // txt/md/json/csv はUTF-8デコード済みテキストをそのまま表示
      return (
        <Box
          component="pre"
          sx={{
            height: '60vh',
            overflow: 'auto',
            m: 0,
            p: 2,
            border: '1px solid #ddd',
            borderRadius: 1,
            backgroundColor: '#fafafa',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {textContent}
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">
          プレビューを表示できません
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" noWrap sx={{ maxWidth: '70%' }}>
          {fileName}
        </Typography>
        <Box>
          {previewUrl && (
            <IconButton onClick={handleOpenInNewTab} title="新しいタブで開く">
              <OpenInNewIcon />
            </IconButton>
          )}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {renderPreviewContent()}
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={() => onDownload(fileKey)}
        >
          ダウンロード
        </Button>
        <Button onClick={onClose}>
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilePreview;
